import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionCardMenu,
  useObjectActions,
  type ObjectKind,
} from "@/object-actions";
import type { ObjectActionHandlers } from "@/object-actions/types";
import type { Folder } from "@cohesive/api-client";
import { WorkspaceResourceFolderCard } from "@cohesive/ui-components";
import type React from "react";
import { useMemo } from "react";

/** Imperative callbacks the registry handlers wrap. */
export interface ResourceFolderActionDeps {
  /** Navigate to the folder's view on the workspace detail page. */
  onOpen: () => void;
  /** Create the folder's resource kind (canvas / chat) inside it. */
  onNewResource: () => void;
  onNewSubfolder: () => void;
  onRename: () => void;
  onArchive: () => void;
}

export function buildResourceFolderKind(folder: Folder): ObjectKind {
  return {
    type: "resource-folder",
    resourceType: folder.resource_type === "canvas" ? "canvas" : "flow-session",
  };
}

/**
 * Registry actions for a canvas/chat folder's menu (kind
 * `resource-folder:canvas|chat`, surface `main-view`) — shared by the
 * detail-page folder card and the list-view folder rows so both render the
 * same manifest-driven menu (Open / New canvas·chat / New folder / Rename /
 * Archive). Dialog-opening handlers defer a tick so the dropdown can finish
 * unmounting (Radix #3317).
 */
export function useResourceFolderActions(
  folder: Folder,
  enabled: boolean,
  deps: ResourceFolderActionDeps,
) {
  const kind = useMemo(() => buildResourceFolderKind(folder), [folder]);
  const isCanvasFolder = folder.resource_type === "canvas";
  const { onOpen, onNewResource, onNewSubfolder, onRename, onArchive } = deps;

  const handlers = useMemo(
    (): ObjectActionHandlers => ({
      open: onOpen,
      "new-canvas": isCanvasFolder
        ? () => window.setTimeout(onNewResource, 0)
        : undefined,
      "new-chat": !isCanvasFolder
        ? () => window.setTimeout(onNewResource, 0)
        : undefined,
      "new-folder": () => window.setTimeout(onNewSubfolder, 0),
      rename: () => window.setTimeout(onRename, 0),
      delete: () => window.setTimeout(onArchive, 0),
    }),
    [
      isCanvasFolder,
      onOpen,
      onNewResource,
      onNewSubfolder,
      onRename,
      onArchive,
    ],
  );

  const { actions } = useObjectActions({
    kind,
    surface: "main-view",
    enabled,
    handlers,
  });

  return { kind, actions };
}

export interface WorkspaceResourceFolderCardItemProps
  extends ResourceFolderActionDeps {
  folder: Folder;
  childCount: number;
  isSelected: boolean;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Registry-backed `WorkspaceResourceFolderCard`. Owns one
 * `useRailRowContextMenu` controller so the card's kebab and right-click open
 * the same manifest-driven menu at the same place they would for any other
 * workspace card.
 */
export function WorkspaceResourceFolderCardItem({
  folder,
  childCount,
  isSelected,
  onClick,
  onOpen,
  onNewResource,
  onNewSubfolder,
  onRename,
  onArchive,
}: WorkspaceResourceFolderCardItemProps) {
  const controller = useRailRowContextMenu();
  const { kind, actions } = useResourceFolderActions(folder, controller.open, {
    onOpen,
    onNewResource,
    onNewSubfolder,
    onRename,
    onArchive,
  });

  return (
    <WorkspaceResourceFolderCard
      name={folder.name}
      childCount={childCount}
      isSelected={isSelected}
      onClick={onClick}
      onDoubleClick={onOpen}
      onContextMenu={controller.onContextMenu}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={kind}
          name={folder.name}
        />
      }
    />
  );
}
